import React from 'react';
import HealthPlanWrapperCollapse from '../../../Components/HealthPlan/HealthPlanWrapper/HealthPlanWrapperCollapse';
import {
	NavigationStepperComponentParams,
	NavigationStepperItemType,
} from '../../../Components/Stepper/NavigationStepper';
import { useAppContext } from '../../../Services/AppContext';
import { useIsLatam } from '../../Region/Services/region.service';
import CurrentPregnancyForm from '../Components/HealthPlanBaby/CreatePlanSections/CurrentPregnancyForm';
import FinalStepForm from '../Components/HealthPlanBaby/CreatePlanSections/FinalStepForm';
import LabResultsForm from '../Components/HealthPlanBaby/CreatePlanSections/LabResultsForm';
import MothersHealthForm from '../Components/HealthPlanBaby/CreatePlanSections/MothersHealthForm';
import PreviousPregnancyForm from '../Components/HealthPlanBaby/CreatePlanSections/PreviousPregnancyForm';
import HealthPlanModal from '../Components/HealthPlanModal';
import HealthPlanQuestionnaireRevision from '../Components/HealthPlanBaby/HealthPlanQuestionnaireRevision';
import HealthPlanUserInformation from '../Components/HealthPlanUserInformation';
import HealthPlanPricing from '../../../Components/HealthPlan/Create/HealthPlanPricing';
import useWorkplaceMeta from '../../Workplace/Hooks/useWorkplaceMeta';
import { HealthPlanCode } from '../../../Enums/HealthPlanCode.enum';
import HealthPlanLoader from '../Components/HealthPlanLoader/HealthPlanLoader';
import { MetaKey } from '../../Meta/Enums/MetaKey.enum';
import FinalStepFormWrapper from '../Components/HealthPlanBaby/CreatePlanSections/FinalStepFormWrapper';

interface HealthPlanStepsParams {
	patientId: string;
	workplaceId: string;
	afterPlanCreate?: (promoCode?: string) => void;
	onHealthPlanCreate?: () => void;
}

const handleUserInformationSuccess = (
	params: NavigationStepperComponentParams,
): void => params.setActiveStepPath('reviseEntryQuestionnaire');

const handleRevisionSuccess = (
	params: NavigationStepperComponentParams,
): void => {
	params.setIsLoading(false);
	params.setActiveStepPath('healthPlanOverview');
};

const useHealthPlanSteps = ({
	patientId,
	workplaceId,
	afterPlanCreate,
	onHealthPlanCreate,
}: HealthPlanStepsParams): NavigationStepperItemType[] => {
	const { globalTemplate } = useAppContext();
	const isLatam = useIsLatam(globalTemplate);
	const { workplaceMeta } = useWorkplaceMeta({ workplaceId });
	const isHealthPlanBabyForFree =
		workplaceMeta[MetaKey.HEALTH_PLAN_BABY_FOR_FREE];

	const handleHealthPlanOverviewSuccess = (
		params: NavigationStepperComponentParams,
	) => {
		if (isHealthPlanBabyForFree) {
			handleFinishHealthPlan(params);
		} else {
			params.setIsLoading(false);
			params.setActiveStepPath('priceSetup');
		}
	};

	const handleFinishHealthPlan = (
		params: NavigationStepperComponentParams,
		promoCode?: string,
	) => {
		params.setIsLoading(true);
		afterPlanCreate?.(promoCode);
		params.setIsLoading(false);
	};

	if (isLatam) {
		const tempSteps: NavigationStepperItemType[] = [
			{
				name: 'component.baby.informationAboutPatient',
				key: 'patientInformation',
				component: (params: NavigationStepperComponentParams) => (
					<HealthPlanUserInformation
						patientId={patientId}
						onSuccess={() => handleUserInformationSuccess(params)}
						setIsLoading={params.setIsLoading}
					/>
				),
			},
			{
				name: 'component.baby.entryQuestionnaireRevision',
				key: 'reviseEntryQuestionnaire',
				component: (params: NavigationStepperComponentParams) => (
					<HealthPlanQuestionnaireRevision
						patientId={patientId}
						onSuccess={() => handleRevisionSuccess(params)}
						setIsLoading={params.setIsLoading}
					/>
				),
			},
			{
				name: 'component.baby.healthPlanOverview',
				key: 'healthPlanOverview',
				component: (params: NavigationStepperComponentParams) => (
					<HealthPlanLoader
						patientId={patientId}
						onSuccess={() => handleHealthPlanOverviewSuccess(params)}
						modalTitle={'component.baby.infoModal.title'}
						modalExtraText={['component.baby.infoModal.extra']}
						modalItems={12}
						modalItemPrefix='component.baby.infoModal.items'
						modalComponent={HealthPlanModal}
						wrapperCollapseTitle={''}
						wrapperCollapse={HealthPlanWrapperCollapse}
						setIsLoading={params.setIsLoading}
						healthPlanCode={HealthPlanCode.BABY}
					/>
				),
				cantGoBack: true,
			},
		];

		if (!isHealthPlanBabyForFree) {
			tempSteps.push({
				name: 'component.createPriceSetup.billing',
				key: 'priceSetup',
				component: (params: NavigationStepperComponentParams) => (
					<HealthPlanPricing
						patientId={patientId}
						onSuccess={(promoCode?: string) =>
							handleFinishHealthPlan(params, promoCode)
						}
						setIsLoading={params.setIsLoading}
						healthPlanCode={HealthPlanCode.BABY}
					/>
				),
				cantGoBack: true,
			});
		}

		return tempSteps;
	}

	return [
		{
			name: 'component.meddiBabySteps.currentPregnancy',
			key: 'currentPregnancy',
			component: ({ nextStep }) => (
				<CurrentPregnancyForm
					onSuccess={() => {
						nextStep();
					}}
					patientId={patientId}
				/>
			),
		},
		{
			name: 'component.meddiBabySteps.previousPregnancy',
			key: 'previousPregnancy',
			component: ({ nextStep }) => (
				<PreviousPregnancyForm
					onSuccess={() => {
						nextStep();
					}}
					patientId={patientId}
				/>
			),
		},
		{
			name: 'component.meddiBabySteps.mothersHealthCondition',
			key: 'mothersHealthCondition',
			component: ({ nextStep }) => (
				<MothersHealthForm
					onSuccess={() => {
						nextStep();
					}}
					patientId={patientId}
				/>
			),
		},
		{
			name: 'component.meddiBabySteps.laboratoryResults',
			key: 'laboratoryResults',
			component: ({ nextStep }) => (
				<LabResultsForm
					onSuccess={() => {
						nextStep();
					}}
					patientId={patientId}
				/>
			),
		},
		{
			name: 'component.meddiBabySteps.finalStep',
			key: 'finalStep',
			component: () => (
				<FinalStepFormWrapper
					patientId={patientId}
					workplaceId={workplaceId}
					onHealthPlanCreate={() => {
						onHealthPlanCreate?.();
					}}
				/>
			),
		},
	];
};

export default useHealthPlanSteps;
